// src/pages/Home.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import Loader from "../components/common/Loader";
import { db } from "../components/firebase/firebase";
import { collection, getDocs } from "firebase/firestore";

const GRADES = ["9", "10", "11", "12"];
const EXAMS = [
  { title: "First Mid Term", key: "first-mid-term" },
  { title: "Quarterly", key: "quarterly" },
  { title: "Second Mid Term", key: "second-mid-term" },
  { title: "Half Yearly", key: "half-yearly" },
  { title: "Third Mid Term", key: "third-mid-term" },
  { title: "First Revision", key: "first-revision" },
  { title: "Second Revision", key: "second-revision" },
  { title: "Third Revision", key: "third-revision" },
  { title: "Model Board", key: "model-board" },
  { title: "Study Materials", key: "study-materials" },
];

function Home() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setGrade] = useState(null);
  const trackRef = useRef(null);
  const navigate = useNavigate();

  // AD MANAGEMENT STATES
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [showAdPopup, setShowAdPopup] = useState(true);

  // FORMS
  const [forms, setForms] = useState([]);

  // Fetch Papers
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "resources"));
        setPapers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch Forms
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "forms"));
        const formsData = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // sort by createdAt desc if present
          .sort((a, b) => {
            const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return tb - ta;
          });
        setForms(formsData);
      } catch (e) {
        console.error("Error fetching forms:", e);
      }
    })();
  }, []);

  // AD LOGIC: Fetch ads and shuffle on component mount
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const snap = await getDocs(collection(db, "ads"));
        const adsData = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Shuffle the ads so a new one is shown each time the page loads
        const shuffledAds = adsData.sort(() => 0.5 - Math.random());
        setAds(shuffledAds);
      } catch (err) {
        console.error("Error fetching ads:", err);
      }
    };
    fetchAds();
  }, []);

  // AD LOGIC: Slideshow timer
  useEffect(() => {
    // Only start the timer if there are multiple ads and the popup is visible
    if (ads.length > 1 && showAdPopup) {
      const timer = setInterval(() => {
        // Cycle to the next ad, and loop back to the beginning
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % ads.length);
      }, 2000); // 2000 milliseconds = 2 seconds
      return () => clearInterval(timer); // Clean up the timer on unmount
    }
  }, [ads, showAdPopup]);

  if (loading) return <Loader />;

  // Get the current ad based on the index
  const activeAd = ads.length > 0 ? ads[currentAdIndex] : null;

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-pink-600 to-pink-400 text-white py-16 sm:py-20">
        <div className="mx-auto max-w-screen-lg px-4 sm:px-6 text-center">
          <motion.h1
            className="mx-auto max-w-[20ch] text-4xl sm:text-6xl font-extrabold mb-3 sm:mb-4 leading-tight tracking-tight break-words"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7 }}
          >
            Welcome To BeConfident
          </motion.h1>
          <motion.p
            className="text-base sm:text-xl opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Your Gateway To Academic Excellence
          </motion.p>
        </div>
      </section>

      {/* Grade Selection */}
      <section className="mx-auto max-w-screen-lg px-4 sm:px-6 py-8 sm:py-10">
        <motion.h2
          className="text-2xl sm:text-3xl font-bold text-pink-600 mb-5 sm:mb-6 text-center"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.45 }}
        >
          Choose Your Class
        </motion.h2>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          {GRADES.map((g, i) => (
            <motion.button
              key={g}
              onClick={() => setGrade(g)}
              className="w-full bg-white rounded-xl shadow-lg p-5 sm:p-6 flex flex-col items-center justify-center focus:outline-none focus:ring-2 focus:ring-pink-400 active:scale-95"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <HiOutlineArrowNarrowRight className="text-pink-600 text-3xl sm:text-4xl mb-2" />
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800">
                Class {g}
              </h3>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Forms Section */}
      <section className="mx-auto max-w-screen-lg px-4 sm:px-6 py-4 sm:py-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-4">
          Forms & Notices
        </h2>

        {forms.length === 0 ? (
          <p className="text-gray-500">No forms available at the moment.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {forms.map((f) => (
              <a
                key={f.id}
                href={f.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-lg shadow-md p-4 flex flex-col justify-between hover:shadow-lg transition"
              >
                <div>
                  <h3 className="font-semibold text-gray-800 truncate">
                    {f.title || "Untitled Form"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(f.createdAt)}{" "}
                    {f.note ? `• ${f.note}` : ""}
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={(e) => {
                      // Allow click on anchor but prevent nested navigation duplication
                    }}
                    className="w-full inline-flex items-center justify-center px-3 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
                  >
                    Open Form <HiOutlineArrowNarrowRight className="ml-2" />
                  </button>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* Exam Types */}
      {selectedGrade && (
        <section className="mx-auto max-w-screen-lg px-4 sm:px-6 py-8">
          <button
            onClick={() => setGrade(null)}
            className="mb-4 text-pink-600 hover:underline text-sm sm:text-base"
          >
            ← Back to Classes
          </button>
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-pink-600 mb-5 sm:mb-6"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.35 }}
          >
            Class {selectedGrade} Papers
          </motion.h2>

          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {EXAMS.map((exam, idx) => (
              <motion.div
                key={exam.key}
                onClick={() =>
                  navigate(`/practice/${selectedGrade}/${exam.key}`)
                }
                className="bg-white rounded-lg shadow-lg p-5 sm:p-6 flex flex-col items-center cursor-pointer select-none"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
              >
                <HiOutlineArrowNarrowRight className="text-pink-600 text-2xl sm:text-3xl mb-2" />
                <span className="text-sm sm:text-base font-medium text-gray-800 text-center">
                  {exam.title}
                </span>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Latest Papers Carousel */}
      <section className="bg-pink-50 py-8 sm:py-10">
        <div className="mx-auto max-w-screen-lg px-4 sm:px-6">
          <motion.h2
            className="text-lg sm:text-2xl font-semibold text-gray-700 mb-4 sm:mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Latest Papers
          </motion.h2>

          <div className="relative">
            <div
              ref={trackRef}
              className="flex gap-3 sm:gap-4 pb-2 sm:pb-4 overflow-x-auto snap-x snap-mandatory no-scrollbar"
            >
              {papers.slice(0, 12).map((p) => (
                <article
                  key={p.id}
                  className="bg-white rounded-lg shadow-md flex-shrink-0 w-52 sm:w-56 snap-start"
                >
                  <div className="h-32 sm:h-36 overflow-hidden rounded-t-lg">
                    <img
                      src={p.thumbnail || "https://via.placeholder.com/400x300"}
                      alt={p.title}
                      className="w-full h-full object-cover max-w-full"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="font-semibold text-gray-800 mb-1 truncate text-sm sm:text-base">
                      {p.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {p.month} {p.year}
                    </p>
                    <Link
                      to={p.link || "#"}
                      className="mt-2 inline-flex items-center text-pink-600 font-medium text-sm"
                    >
                      View <HiOutlineArrowNarrowRight className="ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 🔥 The Ad Popup Component */}
      {showAdPopup && activeAd && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div
            className="bg-white rounded-lg shadow-lg max-w-sm w-full p-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
              onClick={() => setShowAdPopup(false)}
            >
              ✖
            </button>

            {activeAd.imageURL && (
              <img
                src={activeAd.imageURL}
                alt={activeAd.title || "Ad Image"}
                className="w-full h-auto object-cover rounded-md"
                style={{ minHeight: "300px", maxHeight: "500px" }}
              />
            )}
            <h2 className="mt-4 text-center text-xl font-bold">
              {activeAd.title}
            </h2>
          </div>
        </div>
      )}
    </div>
  );
}

export { Home };
export default Home;
